import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import {
  Box, Paper, TextField, IconButton, Typography, Chip,
  CircularProgress, Divider, Avatar, Tooltip, alpha,
  List, ListItem, ListItemButton, ListItemText, ListItemSecondaryAction,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import StorageIcon from '@mui/icons-material/Storage';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  listChatSessions,
  createChatSession,
  getChatSession,
  renameChatSession,
  deleteChatSession,
  projectAiChatInSession,
} from '../../utils/api';

// ── Constants ─────────────────────────────────────────────────────────────────

const SUGGESTED_QUESTIONS = [
  'What is the project scope and objectives?',
  'Who are the key stakeholders and their roles?',
  'What are the main business requirements?',
  'What is the project timeline and key milestones?',
  'Check if any workshop schedules are available for any module',
  'What risks have been identified for this project?',
  'What SAP modules are in scope?',
  'What documents have been uploaded for this project?',
];

const TOOL_LABELS = {
  get_project_info: 'Project Info',
  get_sow_insights: 'Project Insights',
  get_rfp_insights: 'RFP Insights',
  get_project_scope: 'Project Scope',
  get_business_requirements: 'Business Requirements',
  get_stakeholders: 'Stakeholders',
  get_project_timeline: 'Timeline',
  get_integration_map: 'Integration Map',
  get_technical_requirements: 'Technical Requirements',
  get_workshop_schedules: 'Workshop Schedules',
  get_raci_matrix: 'RACI Matrix',
  get_uploaded_documents: 'Documents',
  search_documents: 'Document Search',
  get_discovery_phase: 'Discovery Phase',
  get_bpml_data: 'BPML / BPD',
  get_gap_analysis: 'Gap Analysis',
  get_ricefw_list: 'RICEFW List',
  get_uat_test_scripts: 'UAT Test Scripts',
};

const WELCOME_TITLE = 'Project Intelligence';
const WELCOME_SUBTITLE = "I'm your project assistant for this SAP project. Ask me anything - I can query live data from all phases of the app and your uploaded documents.";

function timeAgo(isoString) {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Markdown renderer ─────────────────────────────────────────────────────────

const mdComponents = {
  h1: ({ children }) => <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>{children}</Typography>,
  h2: ({ children }) => <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>{children}</Typography>,
  h3: ({ children }) => <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 0.5, mb: 0.25 }}>{children}</Typography>,
  p: ({ children }) => <Typography variant="body2" sx={{ mb: 0.75, lineHeight: 1.65 }}>{children}</Typography>,
  ul: ({ children }) => <Box component="ul" sx={{ pl: 2.5, mb: 0.75, mt: 0.25, '& li': { mb: 0.25 } }}>{children}</Box>,
  ol: ({ children }) => <Box component="ol" sx={{ pl: 2.5, mb: 0.75, mt: 0.25, '& li': { mb: 0.25 } }}>{children}</Box>,
  li: ({ children }) => <Typography component="li" variant="body2" sx={{ lineHeight: 1.6 }}>{children}</Typography>,
  strong: ({ children }) => <Box component="strong" sx={{ fontWeight: 700 }}>{children}</Box>,
  em: ({ children }) => <Box component="em">{children}</Box>,
  code: ({ inline, children }) => inline ? (
    <Box component="code" sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 0.5, px: 0.5, fontFamily: 'monospace', fontSize: '0.85em' }}>{children}</Box>
  ) : (
    <Box component="pre" sx={{ bgcolor: 'rgba(0,0,0,0.35)', borderRadius: 1, p: 1.5, overflowX: 'auto', mb: 1, fontSize: '0.8rem', fontFamily: 'monospace' }}><code>{children}</code></Box>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', mb: 1 }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', '& th, & td': { border: '1px solid rgba(255,255,255,0.15)', px: 1, py: 0.5, textAlign: 'left' }, '& th': { bgcolor: 'rgba(245,130,31,0.15)', fontWeight: 700 }, '& tr:nth-of-type(even)': { bgcolor: 'rgba(255,255,255,0.03)' } }}>{children}</Box>
    </Box>
  ),
  blockquote: ({ children }) => <Box sx={{ borderLeft: '3px solid #F5821F', pl: 1.5, ml: 0, mb: 0.75, opacity: 0.85, fontStyle: 'italic' }}>{children}</Box>,
  hr: () => <Divider sx={{ my: 1 }} />,
};

// ── Message bubble ────────────────────────────────────────────────────────────

const MessageBubble = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <Box sx={{ display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row', alignItems: 'flex-start', gap: 1 }}>
      <Avatar sx={{ width: 32, height: 32, bgcolor: isUser ? '#F5821F' : '#2a2a3c', flexShrink: 0, mt: 0.25 }}>
        {isUser ? <PersonIcon sx={{ fontSize: 18 }} /> : <AutoAwesomeIcon sx={{ fontSize: 18, color: '#F5821F' }} />}
      </Avatar>
      <Box sx={{ maxWidth: '80%', minWidth: 0 }}>
        <Paper elevation={1} sx={{ p: isUser ? 1.5 : 2, borderRadius: 2, bgcolor: isUser ? '#F5821F' : 'background.paper', borderTopRightRadius: isUser ? 0 : 2, borderTopLeftRadius: isUser ? 2 : 0, border: isUser ? 'none' : '1px solid rgba(255,255,255,0.06)' }}>
          {isUser ? (
            <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{msg.content}</ReactMarkdown>
          )}
        </Paper>
        {!isUser && (msg.toolsCalled?.length > 0 || msg.sources?.length > 0) && (
          <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            {msg.toolsCalled?.length > 0 && (
              <>
                <Tooltip title="Data sources queried"><StorageIcon sx={{ fontSize: 13, color: 'text.disabled', mr: 0.25 }} /></Tooltip>
                {msg.toolsCalled.map((t) => (
                  <Chip key={t} label={TOOL_LABELS[t] || t} size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: alpha('#F5821F', 0.1), color: '#F5821F', border: '1px solid', borderColor: alpha('#F5821F', 0.3) }} />
                ))}
              </>
            )}
            {msg.sources?.length > 0 && (
              <>
                <DescriptionIcon sx={{ fontSize: 13, color: 'text.disabled', ml: 0.5, mr: 0.25 }} />
                {msg.sources.map((src, si) => (
                  <Chip key={si} label={src} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                ))}
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Sessions sidebar ──────────────────────────────────────────────────────────

const SessionsSidebar = ({ sessions, activeId, onSelect, onCreate, onDelete, onRename, loadingSessions }) => {
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [hoveredId, setHoveredId] = useState(null);

  const startEdit = (e, session) => {
    e.stopPropagation();
    setEditingId(session.session_id);
    setEditTitle(session.title);
  };

  const commitEdit = (e, sessionId) => {
    e.stopPropagation();
    if (editTitle.trim()) onRename(sessionId, editTitle.trim());
    setEditingId(null);
  };

  const cancelEdit = (e) => {
    e.stopPropagation();
    setEditingId(null);
  };

  return (
    <Box sx={{ width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid rgba(255,255,255,0.08)', height: '100%' }}>
      {/* New Chat button */}
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Box
          onClick={onCreate}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5, border: '1px dashed rgba(245,130,31,0.4)', cursor: 'pointer', '&:hover': { bgcolor: alpha('#F5821F', 0.08), borderColor: '#F5821F' }, transition: 'all 0.15s' }}
        >
          <AddIcon sx={{ fontSize: 18, color: '#F5821F' }} />
          <Typography variant="body2" sx={{ color: '#F5821F', fontWeight: 600, fontSize: '0.82rem' }}>New Chat</Typography>
        </Box>
      </Box>

      <Typography variant="caption" sx={{ px: 2, color: 'text.disabled', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
        Your conversations
      </Typography>

      {/* Session list */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', mt: 0.5, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 } }}>
        {loadingSessions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}><CircularProgress size={20} sx={{ color: '#F5821F' }} /></Box>
        ) : sessions.length === 0 ? (
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>No conversations yet</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {sessions.map((s) => (
              <ListItem
                key={s.session_id}
                disablePadding
                onMouseEnter={() => setHoveredId(s.session_id)}
                onMouseLeave={() => setHoveredId(null)}
                sx={{ mb: 0.25 }}
              >
                {editingId === s.session_id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, width: '100%' }}>
                    <TextField
                      size="small"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(e, s.session_id); if (e.key === 'Escape') cancelEdit(e); }}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.5 } }}
                    />
                    <IconButton size="small" onClick={(e) => commitEdit(e, s.session_id)}><CheckIcon sx={{ fontSize: 14, color: 'success.main' }} /></IconButton>
                    <IconButton size="small" onClick={cancelEdit}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
                  </Box>
                ) : (
                  <ListItemButton
                    selected={s.session_id === activeId}
                    onClick={() => onSelect(s.session_id)}
                    sx={{ borderRadius: 1, mx: 0.5, py: 0.75, '&.Mui-selected': { bgcolor: alpha('#F5821F', 0.12), '&:hover': { bgcolor: alpha('#F5821F', 0.18) } }, '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
                  >
                    <ChatIcon sx={{ fontSize: 14, mr: 1, color: s.session_id === activeId ? '#F5821F' : 'text.disabled', flexShrink: 0 }} />
                    <ListItemText
                      primary={s.title}
                      secondary={timeAgo(s.updated_at)}
                      primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: s.session_id === activeId ? 600 : 400, noWrap: true, color: s.session_id === activeId ? '#F5821F' : 'text.primary' }}
                      secondaryTypographyProps={{ fontSize: '0.65rem' }}
                      sx={{ mr: hoveredId === s.session_id ? 4 : 0, minWidth: 0 }}
                    />
                    {hoveredId === s.session_id && (
                      <ListItemSecondaryAction sx={{ display: 'flex', gap: 0.25 }}>
                        <Tooltip title="Rename">
                          <IconButton size="small" onClick={(e) => startEdit(e, s)} sx={{ p: 0.25 }}>
                            <EditIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(s.session_id); }} sx={{ p: 0.25 }}>
                            <DeleteIcon sx={{ fontSize: 13, color: 'error.main' }} />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    )}
                  </ListItemButton>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const ProjectIntelligencePage = () => {
  const { project } = useOutletContext() || {};
  const { projectId } = useParams();

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(true);

  // Chat
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const bottomRef = useRef(null);

  // ── Load sessions on mount ──────────────────────────────────────────────────
  const fetchSessions = useCallback(async () => {
    if (!projectId) return;
    setLoadingSessions(true);
    try {
      const res = await listChatSessions(projectId);
      setSessions(res.data.sessions || []);
    } catch (e) {
      console.error('fetchSessions error', e);
    } finally {
      setLoadingSessions(false);
    }
  }, [projectId]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // ── Auto-scroll ─────────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // ── Select a session ────────────────────────────────────────────────────────
  const selectSession = useCallback(async (sessionId) => {
    setActiveSessionId(sessionId);
    setError('');
    try {
      const res = await getChatSession(projectId, sessionId);
      setMessages(res.data.session.messages || []);
    } catch (e) {
      console.error('getChatSession error', e);
    }
  }, [projectId]);

  // ── Create new session ──────────────────────────────────────────────────────
  const createSession = useCallback(async () => {
    try {
      const res = await createChatSession(projectId);
      const newSession = res.data.session;
      setSessions((prev) => [newSession, ...prev]);
      setActiveSessionId(newSession.session_id);
      setMessages([]);
      setError('');
    } catch (e) {
      console.error('createChatSession error', e);
    }
  }, [projectId]);

  // ── Delete session ──────────────────────────────────────────────────────────
  const deleteSession = useCallback(async (sessionId) => {
    try {
      await deleteChatSession(projectId, sessionId);
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setMessages([]);
      }
    } catch (e) {
      console.error('deleteSession error', e);
    }
  }, [projectId, activeSessionId]);

  // ── Rename session ──────────────────────────────────────────────────────────
  const renameSession = useCallback(async (sessionId, title) => {
    try {
      await renameChatSession(projectId, sessionId, title);
      setSessions((prev) => prev.map((s) => s.session_id === sessionId ? { ...s, title } : s));
    } catch (e) {
      console.error('renameSession error', e);
    }
  }, [projectId]);

  // ── Send message ────────────────────────────────────────────────────────────
  const sendMessage = async (text) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;

    // Auto-create session if none active
    let currentSessionId = activeSessionId;
    if (!currentSessionId) {
      try {
        const res = await createChatSession(projectId);
        const newSession = res.data.session;
        setSessions((prev) => [newSession, ...prev]);
        currentSessionId = newSession.session_id;
        setActiveSessionId(currentSessionId);
      } catch (e) {
        setError('Could not create a session. Please try again.');
        return;
      }
    }

    setInput('');
    setError('');

    const userMsg = { role: 'user', content: userText, sources: [], toolsCalled: [] };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setLoading(true);

    try {
      const history = updatedMessages.slice(-16).map((m) => ({ role: m.role, content: m.content }));
      const response = await projectAiChatInSession(projectId, currentSessionId, userText, history);
      const data = response.data;

      if (data?.success) {
        const assistantMsg = {
          role: 'assistant',
          content: data.answer,
          sources: data.sources || [],
          toolsCalled: data.tools_called || [],
        };
        setMessages((prev) => [...prev, assistantMsg]);
        // Update sidebar: title + updated_at
        setSessions((prev) => prev.map((s) =>
          s.session_id === currentSessionId
            ? { ...s, title: s.title === 'New Chat' ? userText.slice(0, 80) : s.title, updated_at: new Date().toISOString(), message_count: (s.message_count || 0) + 2 }
            : s
        ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)));
      } else {
        setError(data?.error || 'Failed to get a response. Please try again.');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const showSuggestions = messages.length === 0 && !loading;

  if (!project) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress sx={{ color: '#F5821F' }} /></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', maxHeight: '900px' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ background: 'linear-gradient(135deg, #1D2B3F 0%, #243447 100%)', borderRadius: 2, p: 1.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, border: '1px solid rgba(245,130,31,0.3)' }}>
        <Avatar sx={{ bgcolor: alpha('#F5821F', 0.15), border: '1px solid rgba(245,130,31,0.4)', width: 36, height: 36 }}>
          <AutoAwesomeIcon sx={{ color: '#F5821F', fontSize: 20 }} />
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2, fontSize: '1rem' }}>Project Intelligence</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>{project.name}</Typography>
        </Box>
        <Chip icon={<StorageIcon sx={{ fontSize: '14px !important' }} />} label="Live data · All project sources" size="small" sx={{ bgcolor: alpha('#F5821F', 0.12), color: '#F5821F', border: '1px solid rgba(245,130,31,0.3)', fontSize: '0.68rem', height: 24 }} />
      </Paper>

      {/* Body: sidebar + chat */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', bgcolor: 'background.paper', borderRadius: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Sessions sidebar */}
        <SessionsSidebar
          sessions={sessions}
          activeId={activeSessionId}
          onSelect={selectSession}
          onCreate={createSession}
          onDelete={deleteSession}
          onRename={renameSession}
          loadingSessions={loadingSessions}
        />

        {/* Chat area */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
          {/* Messages */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, p: 2, '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(245,130,31,0.3)', borderRadius: 3 } }}>
            {/* Empty state — no session selected */}
            {!activeSessionId && messages.length === 0 && !loading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, textAlign: 'center', py: 6, px: 4 }}>
                <Avatar sx={{ bgcolor: alpha('#F5821F', 0.1), width: 56, height: 56, mb: 2, border: '1px solid rgba(245,130,31,0.3)' }}>
                  <AutoAwesomeIcon sx={{ fontSize: 28, color: '#F5821F' }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{WELCOME_TITLE}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 360, lineHeight: 1.7 }}>
                  {WELCOME_SUBTITLE}
                </Typography>
              </Box>
            )}

            {messages.map((msg, idx) => <MessageBubble key={idx} msg={msg} />)}

            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#2a2a3c', flexShrink: 0 }}><AutoAwesomeIcon sx={{ fontSize: 18, color: '#F5821F' }} /></Avatar>
                <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, borderTopLeftRadius: 0, bgcolor: 'background.paper', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} sx={{ color: '#F5821F' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Querying project data…</Typography>
                </Paper>
              </Box>
            )}

            {/* Suggested questions */}
            {showSuggestions && (
              <Box sx={{ mt: 'auto' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, display: 'block' }}>Try asking:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <Chip key={q} label={q} clickable size="small" onClick={() => sendMessage(q)}
                      sx={{ borderColor: alpha('#F5821F', 0.5), color: '#F5821F', bgcolor: alpha('#F5821F', 0.05), fontSize: '0.72rem', border: '1px solid', '&:hover': { bgcolor: alpha('#F5821F', 0.12) } }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {error && <Typography variant="body2" color="error" sx={{ px: 1 }}>⚠ {error}</Typography>}
            <div ref={bottomRef} />
          </Box>

          <Divider sx={{ opacity: 0.3 }} />

          {/* Input */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 1.5 }}>
            <TextField
              fullWidth multiline maxRows={4}
              placeholder={activeSessionId ? 'Ask anything about this project… (Shift+Enter for new line)' : 'Start typing to create a new chat…'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '0.875rem', '&.Mui-focused fieldset': { borderColor: '#F5821F' }, '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' } } }}
            />
            <Tooltip title={loading ? 'Processing…' : 'Send (Enter)'}>
              <span>
                <IconButton onClick={() => sendMessage()} disabled={loading || !input.trim()}
                  sx={{ bgcolor: '#F5821F', color: '#fff', width: 44, height: 44, '&:hover': { bgcolor: '#e06b10' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }, flexShrink: 0 }}>
                  {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectIntelligencePage;
